<template>
  <div :id="id" class="course-carousel">
    <splide
      :options="splideOptions"
      @splide:mounted="onSplideMounted"
      @splide:active="onSplideActive"
    >
      <splide-slide v-for="(item, index) in items" :key="index" class="">
        <figure
          :class="
            'block relative z-10 course-carousel__slide course-carousel__slide--per-slide-' +
            itemsPerSlide
          "
          aria-labelledby=""
        >
          <div v-if="item.type == 'video' && videoExpanded" class="m-hero-video">
            <button
              class="absolute top-0 right-0 bg-accent-blue w-8 xl:w-16"
              title="Close video player"
              v-on:click="closeVideo"
              @click.prevent="close"
            >
              <icon
                icon="close"
                class="w-8 mr-auto ml-auto h-8 xl:h-16 text-white group-hover:text-grey"
              />
            </button>
            <youtube
              ref="youtube"
              :fit-parent="true"
              :resize="true"
              :player-vars="playerVars"
              :video-id="item.videoId"
              :height="videoHeight"
              :width="videoWidth"
              @ended="onVideoEnd"
            />
          </div>
          <div
            v-if="item.type == 'video'"
            class="course-carousel__video no-underline relative flex flex-wrap items-end mb-5"
            v-on:click="showVideo"
          >
            <img class="w-full rounded-t-lg" :src="item.url" :alt="item.title" />
            <span
              class="absolute block center h-12 w-12 md:h-20 md:w-20 no-underline bg-black-transparent z-10 p-3 flex items-center justify-center rounded-bl-xlg"
              aria-label="Play video"
              role="button"
            >
              <icon
                icon="play"
                role="button"
                width="27"
                height="32"
                class="text-white h-5 w-5 md:h-8 md:w-8"
              />
            </span>
          </div>
          <img
            v-if="item.type == 'image'"
            class="course-carousel__image block p-0 mb-5"
            :src="item.url"
            :alt="item.title"
          />
          <!-- eslint-disable vue/no-v-html -->

          <h2
            :id="id + '-title'"
            class="course-carousel__title text-ml text-catalina-blue mb-1"
            v-if="item.title !== ''"
            v-html="item.title"
          />
          <p
            :id="id + '-caption'"
            class="course-carousel__caption mb-0"
            v-if="item.caption !== ''"
            v-html="item.caption"
          />
        </figure>
      </splide-slide>
    </splide>

    <div class="splide__arrows flex items-center justify-center mt-6">
      <button
        type="button"
        class="splide__arrow splide__arrow--prev flex justify-center items-center rounded-full border border-grey-light-3 w-12 h-12 block"
        aria-label="Previous slide"
        v-on:click="prevSlide"
        data-event="henley-splide-prev-course-carousel"
        :data-target="id"
      >
        <icon
          icon="chevron-left"
          role="presentation"
          width="24"
          height="24"
          class="w-6 h-6 text-catalina-blue"
        />
      </button>
      <div class="text-catalina-blue text-md mx-5-1">{{ slideIndex }}/{{ items.length }}</div>
      <button
        type="button"
        class="splide__arrow splide__arrow--next flex justify-center items-center rounded-full border border-grey-light-3 w-12 h-12 block"
        aria-label="Next slide"
        v-on:click="nextSlide"
        data-event="henley-splide-next-course-carousel"
        :data-target="id"
      >
        <icon
          icon="chevron-right"
          role="presentation"
          width="24"
          height="24"
          class="w-6 h-6 text-catalina-blue"
        />
      </button>
    </div>
  </div>
</template>
<style>
/* purgecss start ignore */
.splide__track {
  overflow: visible !important;
}

/* purgecss end ignore */
</style>
<script>
import Vue from "vue";
import VueYoutube from "vue-youtube";
import Icon from "@/components/atoms/icon/Icon";

Vue.use(VueYoutube);

export default {
  components: {
    Icon,
  },
  props: {
    id: {
      type: String,
      default: () => `card-slider-${Math.floor(Math.random() * 9999999999999)}`,
    },
    items: {
      type: Array,
      required: true,
    },
    itemsPerSlide: {
      type: Number,
      default: () => 1,
    },
  },
  data() {
    return {
      videoExpanded: false,
      videoPlaying: false,
      videoHeight: this.itemsPerSlide == 2 ? 360 : 356,
      videoWidth: this.itemsPerSlide == 2 ? 360 : 600,
      slideIndex: 1,
      splideOptions: {
        fixedWidth: this.itemsPerSlide == 2 ? 360 : 600,
        perPage: this.itemsPerSlide == 2 ? 2 : 1,
        gap: "1.5rem",
        arrows: false,
        pagination: false,
        type: "loop",
        focus: this.itemsPerSlide == 2 ? "left" : "center",
        breakpoints: {
          600: {
            fixedWidth: false,
            perPage: 1,
          },
        },
      },
    };
  },
  computed: {
    itemCount() {
      return this.items.length;
    },
    playerVars() {
      return {
        autoplay: 1,
        enablejsapi: 1,
        playsinline: 1,
        modestbranding: 1,
        rel: 0,
        color: "white",
      };
    },
  },
  watch: {
    isPlaying(shouldPlay) {
      return shouldPlay ? this.playVideo() : this.pauseVideo();
    },
  },
  methods: {
    onSplideMounted(splide) {
      window.addEventListener(`henley-splide-prev-course-carousel-${this.id}`, () => {
        splide.go("-");
      });
      window.addEventListener(`henley-splide-next-course-carousel-${this.id}`, () => {
        splide.go("+");
      });
    },
    onSplideActive(splide) {
      this.slideIndex = splide.index + 1;
    },
    prevSlide() {
      window.dispatchEvent(new CustomEvent(`henley-splide-prev-course-carousel-${this.id}`));
    },
    nextSlide() {
      window.dispatchEvent(new CustomEvent(`henley-splide-next-course-carousel-${this.id}`));
    },
    showVideo() {
      this.videoExpanded = true;
    },
    closeVideo() {
      this.videoExpanded = false;
      this.$emit("close");
    },
    onVideoEnd() {
      this.$emit("end");
    },
    async playVideo() {
      if (this.$refs.youtube) {
        this.videoPlaying = true;
        await this.$refs.youtube.player.playVideo();
      }
    },
    async pauseVideo() {
      if (this.$refs.youtube) {
        this.videoPlaying = false;
        await this.$refs.youtube.player.pauseVideo();
      }
    },
  },
};
</script>
